import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { getApiBaseUrl } from '../../config/api';
import { resolveAvatarUrl, handleAvatarError } from '../../utils/profilePicUrl';
import AccountTypeModal from '../AccountTypeModal';
import { resolveGoogleLogin } from '../../utils/googleAuth';
import './SurveyPanel.css';
import '../ActionMenu.css';

const API_URL = getApiBaseUrl();
const COMMENT_MAX = 500;

const apiUrl = (path) => `${API_URL}/${String(path).replace(/^\/+/, '')}`;

const RATING_OPTIONS = [
  { value: 1, label: 'Very Dissatisfied', emoji: '😞', color: '#94a3b8' },
  { value: 2, label: 'Dissatisfied', emoji: '😕', color: '#fb923c' },
  { value: 3, label: 'Neutral', emoji: '😐', color: '#facc15' },
  { value: 4, label: 'Satisfied', emoji: '🙂', color: '#4ade80' },
  { value: 5, label: 'Very Satisfied', emoji: '😄', color: '#3b82f6' },
];

const IconList = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconClipboard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 5h6a2 2 0 0 1 2 2v1h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V7a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" />
    <path d="M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1H9V5Z" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const IconJoin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0Z" stroke="currentColor" strokeWidth="1.8" />
    <path d="M4.5 19.5a7.5 7.5 0 0 1 15 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M19 8v4M21 10h-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconStar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const IconClock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconPencil = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M13.5 6.5l3 3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 7h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const IconLink = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.07 0l1.42-1.41a5 5 0 0 0-7.07-7.07L10 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M14 11a5 5 0 0 0-7.07 0L5.5 12.42a5 5 0 0 0 7.07 7.07L14 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconSend = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M22 2 11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M22 2 15 22l-4-9-9-4 20-7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const IconEye = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const IconLock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5 10 17.5 19 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StarRow = ({ value, size = 12 }) => (
  <span className="sp-rating-stars" aria-hidden="true">
    {[1, 2, 3, 4, 5].map((i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24">
        <path
          d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
          fill={i <= value ? '#f59e0b' : '#e2e8f0'}
        />
      </svg>
    ))}
  </span>
);

const StarsBadge = ({ value, size = 14 }) => {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span
      aria-label={`${v} out of 5`}
      title={`${v} / 5`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 999,
        padding: '3px 8px',
      }}
    >
      <StarRow value={v} size={size} />
      <span style={{ fontSize: 12, color: '#334155', fontWeight: 700 }}>{v}</span>
    </span>
  );
};

const SurveyPanel = ({ user = null, page = 'main', adminMode = false, surveyId = null }) => {
  const [surveys, setSurveys] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [question, setQuestion] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [targetPage, setTargetPage] = useState('both');
  const [activeSurveyId, setActiveSurveyId] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState('');
  const [fiveStarOnly, setFiveStarOnly] = useState(false);
  const [latestFirst, setLatestFirst] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showAllFeedback, setShowAllFeedback] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [pendingGoogle, setPendingGoogle] = useState(null);
  const [accountTypeLoading, setAccountTypeLoading] = useState(false);
  const menuRef = useRef(null);
  const roleNorm = String(user?.typeofuser || user?.role || '').trim().toLowerCase();
  const isAdminUser = adminMode || ['admin', 'super admin', 'superadmin'].includes(roleNorm);
  const isLoggedIn = Boolean(user?.id);

  const getMediaSrc = (mediaUrl) => {
    if (!mediaUrl) return '';
    if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) return mediaUrl;
    return apiUrl(mediaUrl);
  };

  const relativeTime = (dateString) => {
    if (!dateString) return '';
    const created = new Date(String(dateString).replace(' ', 'T'));
    const diffSec = Math.floor((Date.now() - created.getTime()) / 1000);
    if (Number.isNaN(diffSec)) return dateString;
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} mins ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hrs ago`;
    return `${Math.floor(diffSec / 86400)} days ago`;
  };

  const fetchSurveys = async () => {
    try {
      const params = new URLSearchParams();
      if (surveyId) {
        params.set('survey_id', String(surveyId));
      } else if (!isAdminUser && page && String(page).trim() !== '') {
        params.set('page', String(page).trim());
      }

      const candidates = [`${apiUrl('get_active_surveys.php')}?${params.toString()}`];

      let data = null;
      let lastError = '';
      for (const endpoint of candidates) {
        try {
          const res = await fetch(endpoint);
          const raw = await res.text();
          const parsed = JSON.parse(raw);
          if (!res.ok) {
            lastError = `HTTP ${res.status}`;
            continue;
          }
          data = parsed;
          break;
        } catch (e) {
          lastError = e?.message || 'Network error';
        }
      }

      if (!data) {
        setStatus(`Failed to load surveys (${lastError || 'endpoint not reachable'}).`);
        setSurveys([]);
        return;
      }

      if (data.success) {
        const list = data.surveys || [];
        setSurveys(list);
        if (surveyId && list.length > 0) {
          setActiveSurveyId(Number(surveyId));
        }
        if (!list.length) {
          setStatus('');
        }
      } else {
        setStatus(data.message || 'Failed to load surveys.');
        setSurveys([]);
      }
    } catch (err) {
      setStatus('Failed to load surveys.');
      setSurveys([]);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, [page, adminMode, surveyId, isAdminUser]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setShowAllFeedback(false);
    setShowMenu(false);
  }, [activeSurveyId]);

  useEffect(() => {
    if (!surveyModalOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setSurveyModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [surveyModalOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const activeSurvey = useMemo(() => {
    if (!surveys.length) return null;
    if (activeSurveyId) {
      return surveys.find((s) => Number(s.id) === Number(activeSurveyId)) || null;
    }
    if (surveyId) {
      return surveys.find((s) => Number(s.id) === Number(surveyId)) || surveys[0];
    }
    return null;
  }, [surveys, activeSurveyId, surveyId]);

  const filteredResponses = useMemo(() => {
    const list = activeSurvey?.responses || [];
    let next = [...list];
    if (fiveStarOnly) next = next.filter((r) => Number(r.rating) === 5);
    next.sort((a, b) => {
      const aTime = new Date(String(a.created_at || '').replace(' ', 'T')).getTime() || 0;
      const bTime = new Date(String(b.created_at || '').replace(' ', 'T')).getTime() || 0;
      return latestFirst ? bTime - aTime : aTime - bTime;
    });
    return next;
  }, [activeSurvey, fiveStarOnly, latestFirst]);

  const starDistribution = useMemo(() => {
    const list = activeSurvey?.responses || [];
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    list.forEach((r) => {
      const n = Number(r.rating);
      if (counts[n] !== undefined) counts[n] += 1;
    });
    const max = Math.max(...Object.values(counts), 1);
    return { counts, max };
  }, [activeSurvey]);

  const fillEditForm = (survey) => {
    if (!survey) return;
    setTitle(survey.title || '');
    setDescription(survey.description || '');
    setQuestion(survey.question || '');
    setTargetPage(survey.target_page || 'both');
    setMediaFile(null);
  };

  const resetCreateForm = () => {
    setTitle('');
    setDescription('');
    setQuestion('');
    setTargetPage('both');
    setMediaFile(null);
    setIsEditing(false);
  };

  const handleCreateSurvey = async (e) => {
    e.preventDefault();
    setStatus('');
    if (!isAdminUser) {
      setStatus('Admin access required to create or edit surveys.');
      return;
    }
    if (!question.trim()) {
      setStatus('Please enter a survey question.');
      return;
    }
    try {
      const body = new FormData();
      body.append('user_id', String(user?.id || ''));
      body.append('title', title.trim());
      body.append('description', description.trim());
      body.append('question', question.trim());
      body.append('target_page', targetPage);
      if (mediaFile) body.append('media', mediaFile);

      const url = isEditing ? apiUrl('update_survey.php') : apiUrl('create_survey.php');
      if (isEditing && activeSurvey?.id) body.append('survey_id', String(activeSurvey.id));

      const res = await fetch(url, { method: 'POST', body });
      const raw = await res.text();
      const data = JSON.parse(raw);
      if (data.success) {
        setStatus(isEditing ? 'Survey updated successfully.' : 'Survey created successfully.');
        resetCreateForm();
        fetchSurveys();
      } else {
        setStatus(data.message || 'Failed to save survey.');
      }
    } catch (err) {
      setStatus('Failed to save survey.');
    }
  };

  const handleDeleteSurvey = async () => {
    if (!activeSurvey?.id) return;
    if (!window.confirm('Archive this survey? It will be hidden from users.')) return;
    setStatus('');
    setShowMenu(false);
    try {
      const body = new URLSearchParams({
        survey_id: String(activeSurvey.id),
        user_id: String(user?.id || ''),
      });
      const res = await fetch(apiUrl('delete_survey.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const raw = await res.text();
      const data = JSON.parse(raw);
      if (data.success) {
        setStatus('Survey archived.');
        setActiveSurveyId(null);
        closeSurveyModal();
        resetCreateForm();
        fetchSurveys();
      } else {
        setStatus(data.message || 'Failed to archive survey.');
      }
    } catch (err) {
      setStatus('Failed to archive survey.');
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!activeSurvey) return;
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    setStatus('');
    try {
      const body = new URLSearchParams({
        survey_id: String(activeSurvey.id),
        rating: String(rating),
        comment,
        user_id: String(user?.id || 0),
      });

      const res = await fetch(apiUrl('submit_survey_response.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const raw = await res.text();
      const data = JSON.parse(raw);
      if (data.success) {
        setComment('');
        setRating(5);
        setStatus('Thanks for your response.');
        fetchSurveys();
      } else {
        setStatus(data.message || 'Failed to send response.');
      }
    } catch (err) {
      setStatus('Failed to send response.');
    }
  };

  const shareLink = activeSurvey ? `${window.location.origin}/survey/${activeSurvey.id}` : '';
  const showSurveyInline = Boolean(surveyId) && Boolean(activeSurvey);

  const openSurvey = (id) => {
    setActiveSurveyId(id);
    setSurveyModalOpen(true);
  };

  const closeSurveyModal = () => {
    setSurveyModalOpen(false);
    setShowMenu(false);
  };

  const completeModalLogin = (nextUser, fallbackEmail = '') => {
    localStorage.setItem('forumUser', JSON.stringify(nextUser));
    localStorage.setItem('userEmail', nextUser?.email || fallbackEmail);
    window.dispatchEvent(new Event('forum-user-updated'));
    setShowLoginModal(false);
    setLoginEmail('');
    setLoginPassword('');
    const targetSurveyId = surveyId || activeSurvey?.id || '';
    const targetPath = targetSurveyId ? `/survey/${targetSurveyId}` : '/survey/';
    window.location.assign(targetPath);
  };

  const handleModalLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setLoginError('Please enter your email and password.');
      return;
    }

    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(apiUrl('login.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: loginEmail.trim(),
          password: loginPassword,
        }).toString(),
      });
      const data = await res.json();
      if (!data?.success || !data?.user) {
        setLoginError(data?.message || 'Login failed.');
        return;
      }
      completeModalLogin(data.user, loginEmail.trim());
    } catch (err) {
      setLoginError('Unable to log in right now. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleModalGoogleSuccess = async (credentialResponse) => {
    try {
      setLoginLoading(true);
      setLoginError('');
      const decoded = jwtDecode(credentialResponse.credential);
      const googleProfile = {
        credential: credentialResponse.credential,
        google_id: decoded.sub,
        email: decoded.email,
        name: decoded.name || '',
        avatar: decoded.picture || decoded.photo || decoded.picture_url || '',
      };
      const result = await resolveGoogleLogin(googleProfile);
      if (result.needsAccountType) {
        setPendingGoogle(result.pending);
        return;
      }
      if (!result.user) {
        setLoginError(result.error || 'Google login failed.');
        return;
      }
      completeModalLogin(result.user, decoded.email || '');
    } catch (err) {
      setLoginError('Google login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAccountTypeConfirm = async ({ typeofuser, password, country, position }) => {
    if (!pendingGoogle) return;
    setAccountTypeLoading(true);
    setLoginError('');
    try {
      const result = await resolveGoogleLogin(pendingGoogle, {
        typeofuser,
        password,
        country,
        position,
      });
      if (!result.user) {
        setLoginError(result.error || 'Could not create your account.');
        return;
      }
      setPendingGoogle(null);
      completeModalLogin(result.user, pendingGoogle.email || '');
    } catch (err) {
      setLoginError('Could not create your account.');
    } finally {
      setAccountTypeLoading(false);
    }
  };

  const responseCount = Number(activeSurvey?.total_responses) || 0;
  const commentLen = Math.min(comment.length, COMMENT_MAX);

  const renderSurveyDetail = () => {
    if (!activeSurvey) return null;
    return (
      <>
          <div className="sp-card">
            <div className="sp-card-top">
              <div>
                <h4 className="sp-card-title">{activeSurvey.title || 'Survey'}</h4>
                {(activeSurvey.description || activeSurvey.question) && (
                  <p className="sp-card-desc">
                    {activeSurvey.description || activeSurvey.question}
                  </p>
                )}
              </div>
              {isAdminUser && (
                <div className="sp-menu-wrap" ref={menuRef}>
                  <button
                    type="button"
                    className="sp-menu-btn"
                    aria-label="Survey options"
                    aria-expanded={showMenu}
                    onClick={() => setShowMenu((v) => !v)}
                  >
                    ⋮
                  </button>
                  {showMenu && (
                    <div className="sp-menu-dropdown am-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className="am-item"
                        onClick={() => {
                          setIsEditing(true);
                          fillEditForm(activeSurvey);
                          setShowMenu(false);
                          closeSurveyModal();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        <span className="am-icon am-icon--edit" aria-hidden="true">
                          <IconPencil />
                        </span>
                        <span className="am-label">Edit</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="am-item"
                        onClick={() => {
                          navigator.clipboard.writeText(shareLink);
                          setStatus('Survey link copied.');
                          setShowMenu(false);
                        }}
                      >
                        <span className="am-icon am-icon--copy" aria-hidden="true">
                          <IconLink />
                        </span>
                        <span className="am-label">Copy link</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="am-item am-item--danger"
                        onClick={handleDeleteSurvey}
                      >
                        <span className="am-icon am-icon--delete" aria-hidden="true">
                          <IconTrash />
                        </span>
                        <span className="am-label">Delete</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {activeSurvey.media_url && (
              /\.(mp4|webm|mov)$/i.test(activeSurvey.media_url) ? (
                <video controls className="sp-media-video">
                  <source src={getMediaSrc(activeSurvey.media_url)} />
                </video>
              ) : (
                <img
                  className="sp-media"
                  src={getMediaSrc(activeSurvey.media_url)}
                  alt="Survey media"
                />
              )
            )}

            <div className="sp-stats">
              <div className="sp-stat">
                <span className="sp-stat-icon"><IconStar /></span>
                <div>
                  <div className="sp-stat-label">
                    Avg rating: {activeSurvey.average_rating ?? '0.0'} / 5
                  </div>
                  <div className="sp-stat-sub">
                    ({responseCount} {responseCount === 1 ? 'response' : 'responses'})
                  </div>
                </div>
              </div>
              <div className="sp-stat">
                <span className="sp-stat-icon"><IconClock /></span>
                <div>
                  <div className="sp-stat-label">Updated</div>
                  <div className="sp-stat-sub">
                    {activeSurvey.updated_at ? relativeTime(activeSurvey.updated_at) : '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form className="sp-section" onSubmit={handleSubmitResponse}>
            <div className="sp-section-head">
              <h5 className="sp-section-title">
                <IconStar />
                Your rating
              </h5>
              <span className="sp-section-hint">How satisfied are you?</span>
            </div>

            <div className="sp-rating-grid" role="radiogroup" aria-label="Survey rating">
              {RATING_OPTIONS.map((opt) => {
                const selected = rating === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={`${opt.value} ${opt.label}`}
                    className={`sp-rating-card${selected ? ' is-selected' : ''}`}
                    onClick={() => setRating(opt.value)}
                  >
                    {selected && (
                      <span className="sp-rating-check" aria-hidden="true">
                        <IconCheck />
                      </span>
                    )}
                    <span className="sp-rating-emoji" style={{ filter: selected ? 'none' : 'grayscale(0.15)' }}>
                      {opt.emoji}
                    </span>
                    <StarRow value={opt.value} size={11} />
                    <span className="sp-rating-label">
                      {opt.value} {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="sp-section-head" style={{ marginTop: 18, marginBottom: 10 }}>
              <h5 className="sp-section-title">
                <IconPencil />
                Add a comment
              </h5>
              <span className="sp-section-hint">(optional)</span>
            </div>

            <div className="sp-comment-wrap">
              <textarea
                className="sp-comment"
                value={comment}
                maxLength={COMMENT_MAX}
                onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
                placeholder="Share your thoughts about this survey..."
              />
              <span className="sp-comment-count">{commentLen} / {COMMENT_MAX}</span>
            </div>

            <button
              type={isLoggedIn ? 'submit' : 'button'}
              className={`sp-btn sp-btn-primary sp-submit${!isLoggedIn ? ' is-guest' : ''}`}
              onClick={!isLoggedIn ? () => setShowLoginModal(true) : undefined}
            >
              <IconSend />
              {isLoggedIn ? 'Submit Feedback' : 'Log in to submit feedback'}
            </button>
          </form>

          <div className="sp-actions">
            <button
              type="button"
              className="sp-btn sp-btn-outline"
              onClick={() => setShowAllFeedback((v) => !v)}
              aria-expanded={showAllFeedback}
            >
              <IconEye />
              {showAllFeedback ? 'Hide all feedback' : 'Show all feedback'}
            </button>
          </div>

          {showAllFeedback && (
            <div className="sp-feedback" style={{ maxHeight: isMobile ? '48vh' : 320 }}>
              <h5>All Feedback</h5>
              <div className="sp-feedback-filters">
                <label className="sp-chip">
                  <input type="checkbox" checked={fiveStarOnly} onChange={(e) => setFiveStarOnly(e.target.checked)} />
                  5-star only
                </label>
                <label className="sp-chip">
                  <input type="checkbox" checked={latestFirst} onChange={(e) => setLatestFirst(e.target.checked)} />
                  Latest first
                </label>
              </div>
              <div className="sp-dist">
                <div className="sp-dist-title">Star Distribution</div>
                {[5, 4, 3, 2, 1].map((s) => {
                  const value = starDistribution.counts[s];
                  const width = `${(value / starDistribution.max) * 100}%`;
                  return (
                    <div key={s} className="sp-dist-row">
                      <div className="sp-dist-label">{s} star</div>
                      <div className="sp-dist-bar">
                        <div className="sp-dist-fill" style={{ width }} />
                      </div>
                      <div className="sp-dist-count">{value}</div>
                    </div>
                  );
                })}
              </div>
              {filteredResponses.length === 0 ? (
                <p style={{ margin: 0, color: '#64748b' }}>No feedback matches the current filter.</p>
              ) : (
                filteredResponses.map((resp) => {
                  const name = resp.username || (resp.user_id ? `User ${resp.user_id}` : 'Guest');
                  return (
                    <div key={resp.id} className="sp-response">
                      <img
                        src={resolveAvatarUrl(resp, API_URL)}
                        alt={name}
                        onError={handleAvatarError}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="sp-response-top">
                          <div className="sp-response-name">{name}</div>
                          <StarsBadge value={resp.rating} size={14} />
                        </div>
                        <div className="sp-response-comment">{resp.comment || 'No comment'}</div>
                        <div className="sp-response-time">{relativeTime(resp.created_at)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          <p className="sp-footer-note">
            <IconLock />
            Your feedback helps us improve.
          </p>
      </>
    );
  };

  return (
    <div className="sp-root">
      <AccountTypeModal
        open={!!pendingGoogle}
        email={pendingGoogle?.email}
        loading={accountTypeLoading}
        onConfirm={handleAccountTypeConfirm}
        onCancel={() => setPendingGoogle(null)}
      />

      {isAdminUser && (
        <form className="sp-admin-form" onSubmit={handleCreateSurvey}>
          <h4>{isEditing ? 'Edit Survey' : 'Create Survey'}</h4>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Survey title" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Survey description" style={{ minHeight: 70 }} />
          <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Rating question" />
          <div className="sp-admin-actions">
            <select value={targetPage} onChange={(e) => setTargetPage(e.target.value)} style={{ padding: 10, borderRadius: 10, border: '1px solid #dbe3ef', width: isMobile ? '100%' : 'auto' }}>
              <option value="both">Main + Guest</option>
              <option value="main">Main only</option>
              <option value="guest">Guest only</option>
            </select>
            <input type="file" accept="image/*,video/*" onChange={(e) => setMediaFile(e.target.files?.[0] || null)} style={{ maxWidth: isMobile ? '100%' : 220, width: isMobile ? '100%' : 'auto' }} />
            <button type="submit" className="sp-btn sp-btn-primary">
              {isEditing ? 'Update Survey' : 'Create Survey'}
            </button>
            {isEditing && (
              <button type="button" className="sp-btn sp-btn-ghost" onClick={resetCreateForm}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      )}

      {surveys.length > 0 && !surveyId && (
        <div>
          <div className="sp-list-head">
            <IconList />
            Survey List ({surveys.length})
          </div>
          <ul className="sp-list" role="list">
            {surveys.map((s) => {
              const selected = Number(activeSurveyId) === Number(s.id);
              const headline = (s.title || '').trim() || 'Survey';
              const snippetSource = (s.description || s.question || '').trim();
              const snippet = snippetSource.length > 180 ? `${snippetSource.slice(0, 177)}…` : snippetSource;
              const replies = Number(s.total_responses) || 0;
              const mediaSrc = s.media_url ? getMediaSrc(s.media_url) : '';
              const isVideo = mediaSrc && /\.(mp4|webm|mov)$/i.test(s.media_url);
              return (
                <li key={s.id}>
                  <article
                    className={`sp-post-card${selected ? ' is-active' : ''}`}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <div className="sp-post-card-head">
                      <span className="sp-list-icon">
                        <IconClipboard />
                      </span>
                      <div>
                        <div className="sp-post-card-kicker">Survey</div>
                        <div className="sp-list-meta">
                          {s.updated_at ? (
                            <span>Updated {relativeTime(s.updated_at)}</span>
                          ) : (
                            <span>Open for responses</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <h3 className="sp-post-card-title">{headline}</h3>
                    {snippet ? <p className="sp-post-card-body">{snippet}</p> : null}

                    {mediaSrc ? (
                      isVideo ? (
                        <video className="sp-post-card-media" controls={false} muted>
                          <source src={mediaSrc} />
                        </video>
                      ) : (
                        <img className="sp-post-card-media" src={mediaSrc} alt="" />
                      )
                    ) : null}

                    <div className="sp-post-card-stats">
                      <span>{replies} {replies === 1 ? 'response' : 'responses'}</span>
                      {s.average_rating ? (
                        <>
                          <span className="sp-dot" aria-hidden="true" />
                          <span>Avg {s.average_rating} / 5</span>
                        </>
                      ) : null}
                    </div>

                    <div className="sp-post-card-actions">
                      <button
                        type="button"
                        className="sp-btn sp-btn-primary sp-join-btn"
                        onClick={() => openSurvey(s.id)}
                      >
                        <IconJoin />
                        Join survey
                      </button>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {surveys.length === 0 && (
        <p className="sp-empty">No active survey yet.</p>
      )}

      {showSurveyInline && renderSurveyDetail()}

      {surveyModalOpen && activeSurvey && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="sp-survey-modal-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label={activeSurvey.title || 'Survey'}
              onClick={closeSurveyModal}
            >
              <div className="sp-survey-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sp-survey-modal-bar">
                  <button
                    type="button"
                    className="sp-survey-modal-close"
                    onClick={closeSurveyModal}
                    aria-label="Close survey"
                  >
                    &times;
                  </button>
                </div>
                {renderSurveyDetail()}
                {status && <p className="sp-status">{status}</p>}
              </div>
            </div>,
            document.body
          )
        : null}

      {status && <p className="sp-status">{status}</p>}

      {showLoginModal && typeof document !== 'undefined' && createPortal(
        <div
          className="sp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Login to submit feedback"
          onClick={() => setShowLoginModal(false)}
        >
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Log in to submit feedback</h4>
            <p>Sign in to continue and post your survey response.</p>

            <form onSubmit={handleModalLogin}>
              <label>Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />

              <label>Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />

              {loginError && (
                <p style={{ marginTop: 0, marginBottom: 10, color: '#b91c1c', fontSize: 13 }}>
                  {loginError}
                </p>
              )}

              <div className="sp-modal-actions">
                <button type="submit" className="sp-btn sp-btn-primary" disabled={loginLoading}>
                  {loginLoading ? 'Logging in...' : 'Log in'}
                </button>
                <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setShowLoginModal(false)}>
                  Cancel
                </button>
              </div>

              <div className="sp-or"><span>OR</span></div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={handleModalGoogleSuccess}
                  onError={() => setLoginError('Google login failed. Please try again.')}
                />
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SurveyPanel;
